/**
 * Vođene filters (APP3 top): search plus the duration filter
 * (Sve / SOS ≤ 5 min / Standardne / Produbljene ≥ 19 min, Q21).
 */
import { Search } from 'lucide-react';
import { Input } from '@/components/ui';
import { useLanguage } from '@/store';
import { cn, getSessionFiltersTranslation } from '@/utils';
import { DURATION_FILTERS, type DurationFilter } from '@/utils/sessionBrowse';
import type { SessionFiltersTranslationKey } from '@/utils/translations';

interface GuidedFiltersProps {
  searchQuery: string;
  duration: DurationFilter;
  onSearchChange: (query: string) => void;
  onDurationChange: (duration: DurationFilter) => void;
}

const DURATION_LABEL_KEYS: Record<DurationFilter, SessionFiltersTranslationKey> = {
  all: 'durationAll',
  sos: 'durationSos',
  standard: 'durationStandard',
  deep: 'durationDeep',
};

export function GuidedFilters({ searchQuery, duration, onSearchChange, onDurationChange }: GuidedFiltersProps) {
  const language = useLanguage();
  const t = (key: SessionFiltersTranslationKey) => getSessionFiltersTranslation(key, language);

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          aria-label={t('searchPlaceholder')}
          placeholder={t('searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-11 pl-10"
        />
      </div>

      <div role="group" aria-label={t('duration')} className="flex flex-wrap gap-2">
        {DURATION_FILTERS.map((value) => {
          const active = duration === value;
          return (
            <button
              key={value}
              type="button"
              aria-pressed={active}
              onClick={() => onDurationChange(value)}
              className={cn(
                'min-h-11 rounded-full border px-4 text-sm font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                active
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-input bg-background hover:bg-accent hover:text-accent-foreground',
              )}
            >
              {t(DURATION_LABEL_KEYS[value])}
            </button>
          );
        })}
      </div>
    </div>
  );
}
